import * as React from 'react';
import { useState } from 'react';
import { X, Save, MapPin, User, Phone, StickyNote, Info, Hash, FileText } from 'lucide-react';
import { Project } from '@shared/types/tsk';

interface ProjectDetailsModalProps {
  project: Project;
  onClose: () => void;
  onSave: (updatedFields: Partial<Project>) => void;
}

const ProjectDetailsModal = ({ project, onClose, onSave }: ProjectDetailsModalProps) => {
  const [formData, setFormData] = useState({
    name: project.name || '',
    address: project.address || '',
    siteClassification: project.siteClassification || '',
    approvalNo: project.approvalNo || '',
    lot: project.lot || '',
    rp: project.rp || '',
    clientName: project.clientName || '',
    clientContact: project.clientContact || '',
    notes: project.notes || ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  const stopPropagation = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
      onPointerDown={stopPropagation}
    >
      <div
        className="w-full max-w-lg glass-panel rounded-[2.5rem] overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 max-h-[90vh] flex flex-col"
        onPointerDown={stopPropagation}
      >
        <div className="px-8 py-6 border-b border-white/5 flex justify-between items-center bg-gradient-to-r from-blue-600/10 to-transparent shrink-0">
          <div>
            <h2 className="text-lg font-black uppercase tracking-widest text-white/90">Project Details</h2>
            <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider">Site & Client Metadata</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white/5 text-white/40 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto no-scrollbar">
          <div className="space-y-4">
            {/* Project Name */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] ml-1">Project Name</label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20"><Info size={16} /></div>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-slate-800/50 border border-white/5 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-bold text-white transition-all outline-none"
                  placeholder="E.g. Johnson Residence Renovation"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] ml-1">Property Address</label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20"><MapPin size={16} /></div>
                <input
                  type="text"
                  value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })}
                  className="w-full bg-slate-800/50 border border-white/5 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-bold text-white transition-all outline-none"
                  placeholder="123 Builder Lane, Suburbia"
                />
              </div>
            </div>

            {/* Site Classification & Approval No */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] ml-1">Site Classification</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20"><FileText size={16} /></div>
                  <input
                    type="text"
                    value={formData.siteClassification}
                    onChange={e => setFormData({ ...formData, siteClassification: e.target.value })}
                    className="w-full bg-slate-800/50 border border-white/5 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-bold text-white transition-all outline-none"
                    placeholder="E.g. Class S"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] ml-1">Approval No.</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20"><Hash size={16} /></div>
                  <input
                    type="text"
                    value={formData.approvalNo}
                    onChange={e => setFormData({ ...formData, approvalNo: e.target.value })}
                    className="w-full bg-slate-800/50 border border-white/5 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-bold text-white transition-all outline-none"
                    placeholder="DA/123/2024"
                  />
                </div>
              </div>
            </div>

            {/* Lot & RP */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] ml-1">Lot</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20"><Hash size={16} /></div>
                  <input
                    type="text"
                    value={formData.lot}
                    onChange={e => setFormData({ ...formData, lot: e.target.value })}
                    className="w-full bg-slate-800/50 border border-white/5 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-bold text-white transition-all outline-none"
                    placeholder="Lot 42"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] ml-1">RP / Plan No.</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20"><FileText size={16} /></div>
                  <input
                    type="text"
                    value={formData.rp}
                    onChange={e => setFormData({ ...formData, rp: e.target.value })}
                    className="w-full bg-slate-800/50 border border-white/5 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-bold text-white transition-all outline-none"
                    placeholder="RP98765"
                  />
                </div>
              </div>
            </div>

            {/* Client Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] ml-1">Client Name</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20"><User size={16} /></div>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={e => setFormData({ ...formData, clientName: e.target.value })}
                    className="w-full bg-slate-800/50 border border-white/5 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-bold text-white transition-all outline-none"
                    placeholder="John Doe"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] ml-1">Contact No/Email</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20"><Phone size={16} /></div>
                  <input
                    type="text"
                    value={formData.clientContact}
                    onChange={e => setFormData({ ...formData, clientContact: e.target.value })}
                    className="w-full bg-slate-800/50 border border-white/5 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-bold text-white transition-all outline-none"
                    placeholder="0400 000 000"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] ml-1">Project Notes</label>
              <div className="relative">
                <div className="absolute left-4 top-4 text-white/20"><StickyNote size={16} /></div>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full bg-slate-800/50 border border-white/5 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-bold text-white transition-all outline-none resize-none"
                  placeholder="Any specific council requirements or site constraints..."
                />
              </div>
            </div>
          </div>

          <div className="pt-4 flex gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-4 bg-slate-800 hover:bg-slate-700 text-white font-black text-xs uppercase tracking-widest rounded-2xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-4 bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-widest rounded-2xl shadow-xl shadow-blue-900/40 flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              <Save size={18} /> Update Info
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectDetailsModal;